import { useEffect, useSyncExternalStore } from "react";

// Reduced motion (release 1.4.0). Animation is off when EITHER the device asks for less motion
// (prefers-reduced-motion) OR the person chose "Reduce motion" on My profile. The personal choice is remembered per
// person in this browser (shared shop-floor PCs: one person's choice never follows the next person), like the Gantt
// filters. When motion is reduced the <html> element carries the `reduce-motion` class, which also stops every CSS
// animation and transition in motion.css, so a component that forgets to ask still behaves.

export const MOTION_PREF_PREFIX = "summitops.motion.reduce";
export const REDUCE_MOTION_CLASS = "reduce-motion";

export function motionPreferenceKey(person: string | null | undefined): string {
  return `${MOTION_PREF_PREFIX}.${(person ?? "anonymous").toLowerCase()}`;
}

type Listener = () => void;
const listeners = new Set<Listener>();
let currentPerson: string | null = null;
let personalReduce = false;

function emit(): void {
  syncHtmlClass();
  for (const l of listeners) l();
}

function systemPrefersReduced(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return Boolean(window.matchMedia("(prefers-reduced-motion: reduce)")?.matches);
  } catch {
    return false;
  }
}

function syncHtmlClass(): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle(REDUCE_MOTION_CLASS, isMotionReduced());
}

/** The stored personal choice for a person (false when nothing is stored or storage is unavailable). */
export function readReduceMotionPreference(person: string | null | undefined): boolean {
  try {
    return window.localStorage.getItem(motionPreferenceKey(person)) === "true";
  } catch {
    return false;
  }
}

/** Save the person's choice and apply it at once (every animated component re-renders). */
export function writeReduceMotionPreference(person: string | null | undefined, reduce: boolean): void {
  try {
    if (reduce) window.localStorage.setItem(motionPreferenceKey(person), "true");
    else window.localStorage.removeItem(motionPreferenceKey(person));
  } catch {
    // Storage can be unavailable (private window); the choice still applies for this visit.
  }
  if ((person ?? null) === currentPerson || currentPerson === null) {
    personalReduce = reduce;
    emit();
  }
}

/** Which person's choice applies (called by the app shell once the person is known). */
export function setMotionPerson(person: string | null | undefined): void {
  currentPerson = person ?? null;
  personalReduce = readReduceMotionPreference(person);
  emit();
}

/** True when animation must be off: the device asks for it or the person chose it. */
export function isMotionReduced(): boolean {
  return personalReduce || systemPrefersReduced();
}

/** True when the person's own choice (not the device) reduces motion. */
export function isPersonalReduceOn(): boolean {
  return personalReduce;
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  let mql: MediaQueryList | null = null;
  const onChange = () => emit();
  if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
    try {
      mql = window.matchMedia("(prefers-reduced-motion: reduce)");
      mql?.addEventListener?.("change", onChange);
    } catch {
      mql = null;
    }
  }
  return () => {
    listeners.delete(listener);
    mql?.removeEventListener?.("change", onChange);
  };
}

/** React hook: true when animation must be off. Re-renders when the device setting or the personal choice changes. */
export function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, isMotionReduced, () => false);
}

/** React hook for the settings switch: the person's own choice. */
export function usePersonalReduceMotion(): boolean {
  return useSyncExternalStore(subscribe, isPersonalReduceOn, () => false);
}

/** Applies the signed-in person's motion choice; used once by the app shell. */
export function useApplyMotionPreference(person: string | null | undefined): void {
  useEffect(() => {
    setMotionPerson(person);
  }, [person]);
}

/** Test helper: forget the person and the choice. */
export function resetMotionPreferenceForTests(): void {
  currentPerson = null;
  personalReduce = false;
  emit();
}
